import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/woocommerce';
import { isMasterHost } from '../lib/storeDomain';

export interface BrandConfig {
  name: string;
  brand_color: string;
  logo_url: string;
  apple_touch_icon_url?: string;
  theme_mode?: string;
  url?: string;
  store_url?: string;
  store_id?: string;
  jwtAuthKey?: string;
  client_app_active?: boolean;
}

interface BrandContextType {
  brandConfig: BrandConfig | null;
  setBrandConfig: (config: BrandConfig | null) => void;
  brandColor: string;
  logoUrl: string;
  storeName: string;
}

const BrandContext = createContext<BrandContextType | undefined>(undefined);

const sharedBrandPromises = new Map<string, Promise<any>>();

export const fetchWhiteLabelConfig = async (slug: string) => {
  const normalizedSlug = slug.toLowerCase();
  const existingPromise = sharedBrandPromises.get(normalizedSlug);
  if (existingPromise) return existingPromise;

  const promise = (async () => {
    try {
      const { data, error } = await supabase
        .from('white_labels')
        .select('name, brand_color, logo_url, apple_touch_icon_url, theme_mode, url, store_id')
        .eq('slug', normalizedSlug)
        .maybeSingle();
      if (error) {
        console.error("Failed to fetch brand config:", error);
        return null;
      }
      return data;
    } catch (err) {
      console.error("Exception fetching brand config:", err);
      return null;
    }
  })();

  sharedBrandPromises.set(normalizedSlug, promise);
  return promise;
};

// Called by Settings after a white-label save so the new logo/color/name is
// picked up in the same session — drops the shared promise, the
// sessionStorage cache and the bootstrap-injected config for that slug.
export const invalidateBrandCache = (slug: string) => {
  const s = (slug || '').toLowerCase();
  if (!s) return;
  sharedBrandPromises.delete(s);
  try { sessionStorage.removeItem(`white_label_${s}`); } catch { /* ignore */ }
  delete (window as any).__brandConfig;
};

export const BrandProvider = ({ children }: { children: ReactNode }) => {
  const [brandConfig, setBrandConfig] = useState<BrandConfig | null>((window as any).__brandConfig || null);
  const [isReady, setIsReady] = useState(false);

  // Re-resolve branding when a white-label save lands — setting brandConfig
  // to null re-triggers the fetchBrand effect below (isReady stays true, so
  // there is no splash flicker).
  useEffect(() => {
    const onUpdated = (e: Event) => {
      const slug = (e as CustomEvent).detail || '';
      if (slug) invalidateBrandCache(String(slug));
      setBrandConfig(null);
    };
    window.addEventListener('white-label-updated', onUpdated);
    return () => window.removeEventListener('white-label-updated', onUpdated);
  }, []);

  useEffect(() => {
    const fetchBrand = async () => {
      // Wait for the anti-flicker bootstrap: on custom domains it resolves
      // __resolvedSlug and __brandConfig asynchronously — extracting the
      // slug before it finishes yields the bogus first hostname label
      // ("app") and a null brand config.
      try {
        const p = (window as any).__bootstrapPromise;
        if (p) await Promise.race([p, new Promise(r => setTimeout(r, 3000))]);
      } catch { /* bootstrap failed — continue with fallbacks */ }

      let slug = '';
      const hostname = window.location.hostname;
      const pathParts = window.location.pathname.split('/').filter(Boolean);

      const isLocal = isMasterHost(hostname);

      if (!isLocal) {
        // On custom domains the first hostname label is meaningless ("app"),
        // so prefer the slug the bootstrap resolved from the store list.
        slug = ((window as any).__resolvedSlug || hostname.split('.')[0]).toLowerCase();
      } else {
        if (pathParts[0]?.toLowerCase() === 'loja' && pathParts[1]) {
          // /loja/{slug} — public storefront route
          slug = pathParts[1].split(':')[0].toLowerCase();
        } else if (pathParts[0] && !['login', 'wallet', 'signup', 'onboarding'].includes(pathParts[0])) {
          slug = pathParts[0].split(':')[0].toLowerCase();
        }
      }

      // Correção Crítica de State Leak (Master Domain)
      const storedSlug = localStorage.getItem('pwa_store_slug');
      if (isLocal && (pathParts.length === 0 || pathParts[0] === 'login') && !storedSlug) {
        setBrandConfig(null);
        document.title = "LojaPilot — Acesso Staff";
        document.documentElement.style.setProperty('--brand', '#10B981');
        document.documentElement.style.setProperty('--brand-color', '#10B981');
        setIsReady(true);
        return;
      }

      // Fator PWA: Recuperar contexto amnésico — apenas quando a app corre
      // instalada (standalone). No browser, "/" no master host é a landing
      // pública: restaurar o slug memorizado aqui aplicava o branding da
      // loja (título, cor, logótipo) na landing da plataforma.
      const isPwa = window.matchMedia('(display-mode: standalone)').matches ||
                    (window.navigator as any).standalone === true;
      if (!slug && isPwa) {
        slug = localStorage.getItem('pwa_store_slug') || '';
      }

      if (!slug) {
        setIsReady(true);
        return; // Rota raiz vazia ou sem white label
      }

      // Gravar no cofre PWA
      localStorage.setItem('pwa_store_slug', slug);

      let data = (window as any).__brandConfig;
      if (!data) {
        const cacheKey = `white_label_${slug}`;
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          data = JSON.parse(cached);
        } else {
          data = await fetchWhiteLabelConfig(slug);
          if (data) sessionStorage.setItem(cacheKey, JSON.stringify(data));
        }
      }

      if (data) {
        setBrandConfig(data);

        // Forçar injeção CSS global imediatamente antes de libertar a UI
        if (data.brand_color) {
          document.documentElement.style.setProperty('--brand', data.brand_color);
          document.documentElement.style.setProperty('--brand-color', data.brand_color);
        }

        // Push DOM updates
        document.title = data.name;

        let appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]') as HTMLMetaElement;
        if (!appleTitle) {
          appleTitle = document.createElement('meta');
          appleTitle.name = "apple-mobile-web-app-title";
          document.head.appendChild(appleTitle);
        }
        appleTitle.content = data.name;

        let appleIcon = document.querySelector('link[rel="apple-touch-icon"]');
        if (!appleIcon) {
          appleIcon = document.createElement('link');
          (appleIcon as HTMLLinkElement).rel = "apple-touch-icon";
          document.head.appendChild(appleIcon);
        }
        (appleIcon as HTMLLinkElement).href = data.apple_touch_icon_url || data.logo_url;

        // Generate Dynamic Manifest. Use an absolute start_url/scope (rather
        // than a path relative to the slug) since the manifest is served via
        // a Blob URL, whose base path browsers cannot reliably resolve
        // relative URLs against — this caused installed PWAs to lose the
        // full store URL. Custom domains/subdomains encode the slug in the
        // hostname itself, so their start_url is just the origin root.
      }
      setIsReady(true);
    };

    if (!brandConfig) {
      fetchBrand();
    } else {
      setIsReady(true);
    }
  }, [brandConfig]);

  const brandColor = brandConfig?.brand_color || '#10B981';
  const logoUrl = brandConfig?.logo_url || "/brand/lojapilot-logo-white.png";
  const storeName = brandConfig?.name || "Store";

  if (!isReady) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: brandConfig?.theme_mode === 'dark' ? '#09090b' : '#ffffff',
          zIndex: 99999,
          transition: 'opacity 0.5s ease-out'
        }}
      >
        {brandConfig?.logo_url ? (
          <img src={brandConfig.logo_url} alt="Loading" style={{ width: '80px', height: '80px', objectFit: 'contain', marginBottom: '24px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
        ) : (
          <div style={{ width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: `4px solid ${brandConfig?.brand_color || '#10B981'}`, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        )}
        <style>{`
          @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: .5; } }
        `}</style>
      </div>
    );
  }

  return (
    <BrandContext.Provider value={{ brandConfig, setBrandConfig, brandColor, logoUrl, storeName }}>
      {children}
    </BrandContext.Provider>
  );
};

export const useBrand = () => {
  const context = useContext(BrandContext);
  if (context === undefined) {
    throw new Error('useBrand must be used within a BrandProvider');
  }
  return context;
};
